import React, { useEffect, useState, useRef, ImgHTMLAttributes, HTMLAttributes } from 'react';

function getFirstSrcFromSourceChildren(children: React.ReactNode): string {
    const first = React.Children.toArray(children).find(
        (c): c is React.ReactElement<{ srcSet?: string }> =>
            React.isValidElement(c) && typeof (c.props as { srcSet?: string })?.srcSet === 'string',
    );
    const srcSet = first?.props?.srcSet ?? '';
    const firstUrl = srcSet.split(/[\s,]/)[0] ?? '';
    return firstUrl;
}

/**
 * Above-fold / LCP images: no lazy deferral, no opacity-0 gate.
 * Use for home hero (widget 1) and PDP primary gallery image.
 */
export const EagerImg = React.forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>((props, ref) => {
    const { loading, fetchPriority, ...rest } = props;
    return <img ref={ref} loading={loading ?? 'eager'} fetchPriority={fetchPriority ?? 'high'} {...rest} />;
});
EagerImg.displayName = 'EagerImg';

export const LazyImg: React.FC<ImgHTMLAttributes<any>> = (props) => {
    const { src, onLoad, ...rest } = props;
    /**
     * `_main.scss` keeps a global `[data-lazy-load] { opacity: 0 }` until
     * `[data-loaded="true"]`. Always emit `data-loaded` so SSR HTML stays
     * visible even when client JS fails to hydrate (e.g. stale Redis HTML
     * pointing at old webpack hashes). Native `loading="lazy"` still defers
     * the network fetch.
     */
    return <img src={src} loading="lazy" onLoad={onLoad} {...rest} data-lazy-load data-loaded="true" />;
};

export const LazyPicture: React.FC<HTMLAttributes<any>> = (props) => {
    const { children, ...rest } = props;
    const ref = useRef<HTMLPictureElement | null>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) setInView(true);
            },
            { rootMargin: '200px' },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const firstUrl = inView ? getFirstSrcFromSourceChildren(children) : '';
    const alt = (rest as { 'data-alt'?: string })['data-alt'] ?? '';

    return (
        <picture ref={ref} {...rest}>
            {children}
            {inView && firstUrl ? <img loading="lazy" alt={alt} src={firstUrl} /> : null}
        </picture>
    );
};

export const LazyBackground: React.FC<{ src: string } & HTMLAttributes<any>> = (props) => {
    const { src, ...rest } = props;
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            el.style.backgroundImage = `url(${src})`;
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && el) {
                    el.style.backgroundImage = `url(${src})`;
                }
            },
            { rootMargin: '200px' },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [src]);

    return (
        <div ref={ref} data-background-image={src} {...rest}>
            &nbsp;
        </div>
    );
};
